import React, { useEffect, useState } from 'react';
import styles from './index.module.less';
import { App, Button, Checkbox, Divider, Form, Input, Modal } from 'antd';
import Link from 'next/link';
import Image from 'next/image';
import { useUserProvider } from '@/context/UserProvider';
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithRedirect,
  OAuthProvider,
  signOut,
  sendEmailVerification,
  getRedirectResult,
} from 'firebase/auth';
import { emailNotification, userUpdate } from '@/lib/service';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Cookies from 'js-cookie';
import { isEmailValid } from '@/shared/utils';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
// import { useConfigProvider } from '@/context/ConfigProvider';
import useScreenSize from '@/lib/hooks/utils/useScreenSize';

const Login = () => {
  const { t } = useTranslation('common');
  const { message } = App.useApp();
  const router = useRouter();

  const [loading, setLoading] = useState(false);

  const [isReadProtocol, setIsReadProtocol] = useState(true);
  const [isMailReception, setIsMailReception] = useState(false);
  // const { isIPhone } = useConfigProvider();
  const { width } = useScreenSize();

  const {
    openLogin,
    onOpenLoginModal,
    isLoginView,
    onChangedLoginView,
    // openLoginResult,
    // onChangedLoginResult,
    onAddToken,
    onRefreshLoginLoading,
    isLoginLoading,
  } = useUserProvider();

  const getToken = async (id: string, signInMethod: string) => {
    const isPhone = /iPhone/i.test(navigator.userAgent) || /Android/i.test(navigator.userAgent);

    onRefreshLoginLoading(isPhone);

    const res = await userUpdate(id, router.locale);

    if (res?.data?.first_login) {
      if (res.code == 0) {
        ga_logEvent(EventName.user_register_Success);
      } else if (res.code !== 10002) {
        ga_logEvent(EventName.user_register_Fail);
      }
    }

    if (res.code == 0) {
      onAddToken(res.data.token, signInMethod, id);

      onOpenLoginModal(false);
      // onChangedLoginResult(true);
      message.success(t('LoggedInSuccessfully'));
      ga_logEvent(EventName.user_Login_Success);
    } else {
      onAddToken(undefined);

      // send email notification
      const auth = getAuth();

      if (res.code === 10002) {
        sendEmailVerification(auth?.currentUser, { url: process.env.NEXT_APP_WEB_HOST })
          ?.then()
          .then(() => {
            message.success(
              t('A verification email has been sent to x111x. Please check your inbox.').replace(
                'x111x',
                auth?.currentUser?.email
              )
            );
          })
          .catch((error) => {
            message.success(
              t('A verification email has been sent to x111x. Please check your inbox.').replace(
                'x111x',
                auth?.currentUser?.email
              )
            );
          })
          .finally(() => {
            onChangedLoginView(true);
            setLoading(false);
            signOut(auth);
          });
      } else {
        message.error(res.message);
        signOut(auth);
      }
    }
    setLoading(false);
    setTimeout(() => {
      onRefreshLoginLoading(false);
    }, 1000);
  };

  const onFinish = (values: any) => {
    const auth = getAuth();
    setLoading(true);

    if (isLoginView) {
      signInWithEmailAndPassword(auth, values?.email, values?.password)
        .then((result) => {
          result?.user?.getIdToken().then((id) => {
            getToken(id, 'email');
            ga_logEvent(EventName.login, { method: 'Email' });
          });
        })
        .catch((error) => {
          onAddToken(undefined);
          message.error(t('Login_Failed_Error_Msg'));
          setLoading(false);
        });
    } else {
      if (!isReadProtocol) {
        message.warning(t('Non-consent agreement'));
        setLoading(false);
        return;
      }
      createUserWithEmailAndPassword(auth, values?.email, values?.password)
        .then((result) => {
          const tempUser = result?.user;

          sendEmailVerification(tempUser, { url: process.env.NEXT_APP_WEB_HOST })
            ?.then()
            .then(() => {
              // message.success(t("A verification email has been sent to x111x. Please check your inbox.").replace('x111x', values?.email));
              // onChangedLoginView(true)
            })
            .catch((error) => {
              // message.error(t('Sign_Up_Failed_Error_Msg'));
            })
            .finally(() => {
              // setLoading(false);
              // signOut(getAuth());
              // onAddToken(undefined);
            });

          result?.user?.getIdToken().then((id) => {
            getToken(id, 'email');
            ga_logEvent(EventName.login, { method: 'Email' });
          });
        })
        .catch((error) => {
          onAddToken(undefined);
          message.error(t('Sign_Up_Failed_Error_Msg'));
          setLoading(false);
        });
    }
    emailNotification(isMailReception).catch((error) => {
      message.error(error.message);
    });
  };

  const onAuth = (method: string) => {
    onOpenLoginModal(false);
    const auth = getAuth();
    let provider;

    if (method === 'Google') {
      provider = new GoogleAuthProvider();
    } else if (method === 'Apple') {
      provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      provider.setCustomParameters({
        locale: router?.locale,
      });
    }

    const isPhone = /iPhone/i.test(navigator.userAgent) || /Android/i.test(navigator.userAgent);

    if (width < 900 && !isPhone) {
      Cookies.set('authLoading', 'true', { expires: 1 });
      onRefreshLoginLoading(true);
      updateQueryParam('authLoading', 'true', false);
      onOpenLoginModal(false);
      signInWithRedirect(auth, provider);
    } else {
      signInWithPopup(auth, provider)
        .then((result) => {
          let credential;
          if (method === 'Google') {
            credential = GoogleAuthProvider.credentialFromResult(result);
          } else {
            credential = OAuthProvider.credentialFromResult(result);
          }
          const token = credential.accessToken;
          Cookies.set('accessToken', token, { expires: 7 });
          // ls_saveItem('accessToken', token);

          result.user.getIdToken().then((id) => {
            getToken(id, 'google');
            ga_logEvent(EventName.login, { method });
          });
        })
        .catch((error) => {
          onAddToken(undefined);
          message.error(t('Third_Login_Failed_Error_Msg').replace('x111x', method));
        });
    }
  };

  const updateQueryParam = (key: string, value: string, shallow: boolean = true) => {
    // 获取当前查询参数
    const newQuery = { ...router.query, [key]: value };

    // 使用 pushState 更新 URL，且不刷新页面
    router.push(
      {
        pathname: router.pathname, // 保持当前路径
        query: newQuery, // 更新查询参数
      },
      undefined,
      { shallow: shallow }
    ); // shallow: true 确保不触发页面刷新
  };

  useEffect(() => {
    return () => {
      if (openLogin) {
        ga_logEvent(EventName.Login_View);
      }
    };
  }, [openLogin]);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = () => {
    const isPhone = /iPhone/i.test(navigator.userAgent) || /Android/i.test(navigator.userAgent);

    if (typeof window !== 'undefined' && isPhone && router?.query?.authLoading === 'true') {
      onRefreshLoginLoading(true);
    }

    const auth = getAuth();
    getRedirectResult(auth)
      .then(async (result) => {
        if (result) {
          if (typeof window !== 'undefined') {
            onOpenLoginModal(false);
            onRefreshLoginLoading(true);
            setTimeout(() => {
              Cookies.remove('authLoading');
              updateQueryParam('authLoading', 'false');
            }, 1000);
          }

          const method = result?.providerId === 'google.com' ? 'Google' : 'Apple';
          let credential;
          if (method === 'Google') {
            credential = GoogleAuthProvider.credentialFromResult(result);
          } else {
            credential = OAuthProvider.credentialFromResult(result);
          }

          const token = credential.accessToken;
          Cookies.set('accessToken', token, { expires: 7 });
          // ls_saveItem('accessToken', token)

          result.user
            .getIdToken()
            .then((id) => {
              getToken(id, method);
              ga_logEvent(EventName.login, { method: method });
            })
            .finally(() => {
              if (typeof window !== 'undefined') {
                onRefreshLoginLoading(false);
              }
            });
        } else {
          if (typeof window !== 'undefined') {
            onRefreshLoginLoading(false);
            setTimeout(() => {
              Cookies.remove('authLoading');
            }, 1000);
          }
        }
      })
      .catch(() => {
        if (typeof window !== 'undefined') {
          onRefreshLoginLoading(false);
        }
      });
  };

  return (
    <>
      <Modal
        className={styles.mainContainer}
        open={openLogin}
        onCancel={() => onOpenLoginModal(false)}
        destroyOnHidden
        zIndex={2001}
      >
        <Form
          name="normal_login"
          className={styles.homeContent}
          initialValues={{ remember: true }}
          onFinish={onFinish}
          style={{ padding: '20px 24px' }}
        >
          <div className={styles.titleContainer}>{isLoginView ? t('Log in') : t('Sign Up')}</div>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: t('pleaseInputYourEmail') },
              () => ({
                validator(_, value) {
                  if (!value || isEmailValid(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('pleaseInputYourEmail')));
                },
              }),
            ]}
          >
            <Input
              className={styles.inputItem}
              prefix={
                <Image src={`/images/user/email-icon.webp`} width={20} height={20} alt={'email'} loading="lazy" />
              }
              placeholder={t('EmailAddress')}
              // type="email"
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[
              { required: true, message: t('pleaseInputYourPassword') },
              () => ({
                validator(_, value) {
                  if (!value || (`${value}`?.length >= 6 && `${value}`?.length <= 16)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('PasswordShouldBe_6_16_Characters')));
                },
              }),
            ]}
          >
            <Input
              className={styles.inputItem}
              prefix={
                <Image src={`/images/user/password-icon.webp`} width={20} height={20} alt={'password'} loading="lazy" />
              }
              type="password"
              placeholder={t('Password')}
            />
          </Form.Item>
          {!isLoginView && (
            <Form.Item
              name="repassword"
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('theNewPasswordThatYouEnteredDoNotMatch')));
                  },
                }),
              ]}
            >
              <Input
                className={styles.inputItem}
                prefix={
                  <Image
                    src={`/images/user/password-icon.webp`}
                    width={20}
                    height={20}
                    alt={'password'}
                    loading="lazy"
                  />
                }
                type="password"
                placeholder={t('ConfirmPassword')}
              />
            </Form.Item>
          )}

          {!isLoginView && (
            <>
              <div className={styles.agreeDesc}>
                <Checkbox checked={isReadProtocol} onChange={() => setIsReadProtocol(!isReadProtocol)} />
                <div>
                  <span>{t('ByClickingContinue_Desc')}</span>
                  <span>
                    <Link href={'/terms-of-use'} className={styles.link} onClick={() => onOpenLoginModal(false)}>
                      {t('Terms')},
                    </Link>
                  </span>
                  <span>
                    <Link href={'/privacy-policy'} className={styles.link} onClick={() => onOpenLoginModal(false)}>
                      {t('privacyPolicy')}
                    </Link>
                  </span>
                </div>
              </div>
            </>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" className={styles.loginFormButton} loading={loading}>
              {isLoginView ? t('Next') : t('Continue')}
            </Button>
          </Form.Item>

          {isLoginView && (
            <Form.Item>
              {/* <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox className={styles.staySignedIn}>{t('StaySignedIn')}</Checkbox>
              </Form.Item> */}

              <Link
                className={styles.forgotItem}
                href="/forgot-password"
                onClick={() => {
                  onOpenLoginModal(false);
                  ga_logEvent(EventName.login_Forgot_Password);
                }}
              >
                {t('ForgotPassword')}?
              </Link>
            </Form.Item>
          )}

          {isLoginView && (
            <Form.Item>
              <Button className={styles.createFormButton} onClick={() => onChangedLoginView(false)}>
                {t('Need an account? Sign Up')}
              </Button>
            </Form.Item>
          )}

          <Form.Item>
            <Divider className={styles.googleLoginText}>
              {isLoginView && <span>{t('Login')}</span>}
              {isLoginView && <span> {t('with others')}</span>}
              {!isLoginView && <span> {t('or create an account with')}</span>}
            </Divider>

            <Button className={styles.googleFormButton} onClick={() => onAuth('Google')}>
              <Image
                src={`/images/login/google_icon.webp`}
                width={20}
                height={20}
                alt={'google login'}
                loading="lazy"
              />
              <span>{t('Login with')}</span>
              <span>google</span>
            </Button>

            <Button className={styles.googleFormButton} style={{ marginTop: '15px' }} onClick={() => onAuth('Apple')}>
              <Image
                src={`/images/login/apple-login-icon.webp`}
                width={20}
                height={20}
                alt={'apple login'}
                loading="lazy"
              />
              <span>{t('Login with')}</span>
              <span>Apple</span>
            </Button>

            {!isLoginView && (
              <div className={styles.accountContainer}>
                <span>{t('AlreadyHaveAnAccount')} </span>
                <span onClick={() => onChangedLoginView(true)}>{t('Log in')}</span>
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Login;
